import api from 'api/client';
import { queryResponseValidator, toQueryStr } from 'helpers/queryUtils';
import { ContextType, MessageRole } from 'types';
import { z } from 'zod';

import { dateSchema } from './shared/common.schema';
import { STATUS } from './types';

const messageRoleSchema = z.nativeEnum(MessageRole);
const contextTypeSchema = z.nativeEnum(ContextType);

const conversationSchema = z.object({
  id: z.number(),
  userId: z.string(),
  coachId: z.string(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
  messageTotal: z.number(),
});

const messageCitationSchema = z.object({
  marker: z.string(),
  toolIndex: z.number(),
  tool: z.string(),
  summary: z.string().nullable(),
  rationale: z.string().nullable(),
});

const messageSchema = z.object({
  id: z.number(),
  role: messageRoleSchema,
  content: z.string(),
  contextType: contextTypeSchema,
  createdAt: dateSchema,
  rating: z.number().nullable().optional(),
  citations: z.array(messageCitationSchema).optional(),
});

export type AssistantMessageCitation = z.infer<typeof messageCitationSchema>;

export type AssistantConversation = z.infer<typeof conversationSchema>;
export type AssistantMessage = z.infer<typeof messageSchema>;

const conversationResponseSchema = z.object({
  status: z.enum([STATUS.OK, STATUS.ERROR]),
  interaction: conversationSchema.nullable(),
});

const messagesResponseSchema = z.object({
  status: z.enum([STATUS.OK, STATUS.ERROR]),
  total: z.number(),
  items: z.array(messageSchema),
});

const ratingResponseSchema = z.object({
  status: z.enum([STATUS.OK, STATUS.ERROR]),
  rating: z.number().nullable(),
});

const traceLLMUsageSchema = z.object({
  promptTokens: z.number(),
  cachedTokens: z.number(),
  completionTokens: z.number(),
  totalTokens: z.number(),
});

// Which DB-backed prompt row a call was composed from. `checksum` is derived
// from the content, so it identifies the same text across environments whose
// `version` counters diverged — staging v7 and prod v7 are unrelated numbers.
const tracePromptAttributionSchema = z.object({
  key: z.string(),
  versionId: z.string(),
  version: z.number(),
  checksum: z.string(),
});

export type AssistantTracePromptAttribution = z.infer<
  typeof tracePromptAttributionSchema
>;

const traceLLMRunSchema = z.object({
  model: z.string(),
  seed: z.number(),
  fingerprint: z.string().nullable(),
  durationMs: z.number().optional(),
  timeToFirstTokenMs: z.number().optional(),
  // Token usage + its frozen USD cost. Optional so traces persisted before
  // cost tracking still parse; costUsd is null when the model had no pricing.
  usage: traceLLMUsageSchema.optional(),
  costUsd: z.number().nullable().optional(),
  // A list, not one entry: ReplyGenerator composes the Rose system prompt with
  // the proactive-brief prompt for a single completion. This is a plain
  // z.object (not passthrough), so without declaring the field here Zod strips
  // it and the drawer never sees it. Optional — runs persisted before
  // attribution, and calls that read no DB-backed prompt, have none.
  prompts: z.array(tracePromptAttributionSchema).optional(),
});

export type AssistantTraceLLMRun = z.infer<typeof traceLLMRunSchema>;

// llmRuns is a list of per-call entries: each carries its `layer` (the call's
// identity — planner / toolPlanner / replyGenerator / …), the run metadata, and
// (optionally) the model's raw completion text for that call.
const traceLLMRunEntrySchema = z.object({
  layer: z.string(),
  run: traceLLMRunSchema,
  // Raw completion text. Optional so streaming callers and traces persisted
  // before output capture still parse.
  output: z.string().optional(),
});

export type AssistantTraceLLMRunEntry = z.infer<typeof traceLLMRunEntrySchema>;

const traceStepTimingSchema = z.object({
  step: z.string(),
  durationMs: z.number(),
});

// Only the timing fields the inspector renders are typed; `.passthrough()`
// keeps the rest of TraceMetrics (toolsUsed, toolMetrics, …) intact. Timing
// fields are optional so traces persisted before timing was added still parse.
const traceMetricsSchema = z
  .object({
    stepTimings: z.array(traceStepTimingSchema).optional(),
    totalDurationMs: z.number().optional(),
  })
  .passthrough();

export type AssistantTraceStepTiming = z.infer<typeof traceStepTimingSchema>;
export type AssistantTraceMetrics = z.infer<typeof traceMetricsSchema>;

const traceLLMMessageSchema = z.object({
  role: z.enum(['system', 'user', 'assistant']),
  content: z.string(),
});

export type AssistantTraceLLMMessage = z.infer<typeof traceLLMMessageSchema>;

// llmInputs is a list of per-call entries: each carries its `layer` and the
// fully-substituted prompt messages sent on that call.
const traceLLMInputEntrySchema = z.object({
  layer: z.string(),
  messages: z.array(traceLLMMessageSchema),
});

export type AssistantTraceLLMInputEntry = z.infer<
  typeof traceLLMInputEntrySchema
>;

const traceToolCallSchema = z
  .object({
    tool: z.string(),
    params: z.record(z.string(), z.unknown()),
    success: z.boolean(),
    summary: z.string().optional(),
    rationale: z.string().optional(),
    recordCount: z.number().optional(),
    executionTime: z.number().optional(),
    dataPreview: z.string(),
    error: z.string().optional(),
  })
  .passthrough();

const traceToolResultFullSchema = z
  .object({
    toolIndex: z.number(),
    data: z.unknown(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
    rationale: z.string().optional(),
    truncated: z.boolean().optional(),
    originalSize: z.number().optional(),
  })
  .passthrough();

const traceSchema = z
  .object({
    id: z.number(),
    messageId: z.number(),
    sessionId: z.string(),
    query: z.string(),
    plan: z.unknown(),
    toolCalls: z.array(traceToolCallSchema),
    toolResultsFull: z.array(traceToolResultFullSchema).nullable(),
    evaluatorVerdict: z
      .object({
        sufficient: z.boolean(),
        reasoning: z.string().optional(),
        additionalToolsRequested: z.number(),
      })
      .passthrough()
      .nullable(),
    metrics: traceMetricsSchema,
    citations: z
      .array(
        z.object({
          marker: z.string(),
          toolIndex: z.number(),
        })
      )
      .nullable(),
    // Lists of per-call entries in execution order. The backend normalizes
    // legacy (pre-migration) fixed-slot rows to this shape before serving.
    llmRuns: z.array(traceLLMRunEntrySchema).nullable(),
    llmInputs: z.array(traceLLMInputEntrySchema).nullable(),
    truncated: z.boolean(),
    createdAt: dateSchema,
    message: z
      .object({
        content: z.string(),
        createdAt: dateSchema,
        interactionId: z.number(),
      })
      .optional(),
  })
  .passthrough();

const traceResponseSchema = z.object({
  status: z.enum([STATUS.OK, STATUS.ERROR]),
  trace: traceSchema,
});

export type AssistantMessageTraceResponse = z.infer<typeof traceResponseSchema>;
export type AssistantMessageTrace = AssistantMessageTraceResponse['trace'];
export type AssistantTraceToolCall = z.infer<typeof traceToolCallSchema>;
export type AssistantTraceToolResultFull = z.infer<
  typeof traceToolResultFullSchema
>;

type ConversationResponse = z.infer<typeof conversationResponseSchema>;
type MessagesResponse = z.infer<typeof messagesResponseSchema>;
type RatingResponse = z.infer<typeof ratingResponseSchema>;
type TraceResponse = z.infer<typeof traceResponseSchema>;

export const fetchConversation = async (
  userId: string
): Promise<ConversationResponse> => {
  return api
    .get(`assistant/conversations/${userId}`)
    .json()
    .then(
      queryResponseValidator<ConversationResponse>(conversationResponseSchema)
    );
};

export const fetchMessages = async (
  interactionId: number,
  params: { before?: number; limit?: number } = {}
): Promise<MessagesResponse> => {
  return api
    .get(
      `assistant/interactions/${interactionId}/messages?${toQueryStr(params)}`
    )
    .json()
    .then(queryResponseValidator<MessagesResponse>(messagesResponseSchema));
};

export const setMessageRating = async (
  interactionId: number,
  messageId: number,
  rating: 1 | -1 | null
): Promise<RatingResponse> => {
  return api
    .patch(
      `assistant/interactions/${interactionId}/messages/${messageId}/rating`,
      { json: { rating } }
    )
    .json()
    .then(queryResponseValidator<RatingResponse>(ratingResponseSchema));
};

export const fetchMessageTrace = async (
  interactionId: number,
  messageId: number
): Promise<TraceResponse> => {
  return api
    .get(`assistant/interactions/${interactionId}/messages/${messageId}/trace`)
    .json()
    .then(queryResponseValidator<TraceResponse>(traceResponseSchema));
};
